import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc, deleteDoc, setDoc, Timestamp } from "firebase/firestore";
import { db, loginAdmin } from "../../Firebase";
import { ToastContainer, toast } from "react-toastify";
import { useSearchParams, useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import { CircleLoader } from "react-spinners";
import * as XLSX from "xlsx";
import ReportedUsers from "../VortexDataAnalysis/ReportedUsers";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 470,
  bgcolor: "white",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const SUBSCRIBED_USER_IDS = [
  "1L7V2KB59IfGxCoDI2crpwrxLw42",
  "43UVQ3QryjNbjaNBN64WH41j2Bw2",
  "ASFMHUuJo5ZaR4zoncIj49SlbJD2",
  "xjlG7UF04DbtkiWMU11YB019x1t1",
  "ypXWu1MoafdYBzaYTVLgcQvpFr13",
];

const todayStr = () => new Date().toISOString().split("T")[0];

const formatExpiry = (ts) => {
  if (!ts) return null;
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleDateString();
};

const User = () => {
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState("");
  const [isSubcribed, setIsSubcribed] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);

  const [ultraActive, setUltraActive] = useState(false);
  const [ultraExpiry, setUltraExpiry] = useState(null);
  const [ultraExpiryInput, setUltraExpiryInput] = useState("");

  const [premiumActive, setPremiumActive] = useState(false);
  const [premiumExpiry, setPremiumExpiry] = useState(null);
  const [premiumExpiryInput, setPremiumExpiryInput] = useState("");
  const [personaVerified, setPersonaVerified] = useState(false);
  const [azureVerified, setAzureVerified] = useState(false);

  const [profiles, setProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const handleOpen = (uid, isSubscribed, blocked, profile) => {
    setOpen(true);
    setUserId(uid);
    setIsSubcribed(isSubscribed || false);
    setIsBlocked(blocked || false);
    setUltraActive(profile.ultraActive || false);
    setUltraExpiry(profile.ultraExpiry || null);
    setUltraExpiryInput("");
    setPremiumActive(profile.premiumActive || false);
    setPremiumExpiry(profile.premiumExpiry || null);
    setPremiumExpiryInput("");
    setPersonaVerified(profile.personaVerified || false);
    setAzureVerified(profile.azureVerified || false);
  };

  const handleClose = () => setOpen(false);

  const fetchData = async () => {
    try {
      await loginAdmin();
      setIsLoading(true);
      const userSnapshot = await getDocs(collection(db, "users"));
      const usersList = userSnapshot.docs.map((doc) => ({
        id: doc.id,
        uid: doc.data().uid || doc.id,
        ...doc.data(),
      }));

      setProfiles(usersList);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users");
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const filter = searchParams.get("filter") || "all";
    let result = profiles;
    if (filter === "subscribed") result = profiles.filter((u) => SUBSCRIBED_USER_IDS.includes(u.id));
    else if (filter === "not_subscribed") result = profiles.filter((u) => !SUBSCRIBED_USER_IDS.includes(u.id));
    else if (filter === "face_verified") result = profiles.filter((u) => !!u.azureVerified);
    else if (filter === "id_verified") result = profiles.filter((u) => !!u.personaVerified);
    if (search) {
      const term = search.toLowerCase();
      result = result.filter((u) =>
        (u.name && u.name.toLowerCase().includes(term)) ||
        (u.email && u.email.toLowerCase().includes(term))
      );
    }
    setFilteredUsers(result);
  }, [searchParams, search, profiles]);

  const handleFilterChange = (e) => setSearchParams({ filter: e.target.value });

  const subscribUserProfile = async (uid, action) => {
    try {
      const userRef = doc(db, "users", uid);
      const isSubscribed = action === "false";
      await updateDoc(userRef, { isSubscribed });
      toast.success(isSubscribed ? "User subscribed" : "User unsubscribed");
      fetchData();
      handleClose();
    } catch (error) {
      toast.error("Failed to update subscription");
    }
  };

  const deleteUserProfile = async (uid) => {
    try {
      await deleteDoc(doc(db, "users", uid));
      toast.success("User deleted successfully");
      fetchData();
      handleClose();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const blockOrUnblock = async (uid, action) => {
    try {
      const userRef = doc(db, "users", uid);
      const blocked = action === "block";
      await updateDoc(userRef, { isBlocked: blocked });
      toast.success(blocked ? "User blocked" : "User unblocked");
      fetchData();
      handleClose();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const grantUltra = async (uid) => {
    if (!ultraExpiryInput) { toast.error("Please select an expiry date"); return; }
    try {
      const expiry = Timestamp.fromDate(new Date(ultraExpiryInput));
      await setDoc(doc(db, "users", uid), { ultraActive: true, ultraExpiry: expiry }, { merge: true });
      toast.success("Ultra granted");
      fetchData();
      handleClose();
    } catch (error) {
      toast.error("Failed to grant Ultra");
    }
  };

  const removeUltra = async (uid) => {
    try {
      await setDoc(doc(db, "users", uid), { ultraActive: false, ultraExpiry: null }, { merge: true });
      toast.success("Ultra removed");
      fetchData();
      handleClose();
    } catch (error) {
      toast.error("Failed to remove Ultra");
    }
  };

  const grantPremium = async (uid) => {
    if (!premiumExpiryInput) { toast.error("Please select an expiry date"); return; }
    try {
      const expiry = Timestamp.fromDate(new Date(premiumExpiryInput));
      await setDoc(doc(db, "users", uid), { premiumActive: true, premiumExpiry: expiry }, { merge: true });
      toast.success("Premium granted");
      fetchData();
      handleClose();
    } catch (error) {
      toast.error("Failed to grant Premium");
    }
  };

  const removePremium = async (uid) => {
    try {
      await setDoc(doc(db, "users", uid), { premiumActive: false, premiumExpiry: null }, { merge: true });
      toast.success("Premium removed");
      fetchData();
      handleClose();
    } catch (error) {
      toast.error("Failed to remove Premium");
    }
  };

  const fetchSingleProfile = (uid) => {
    const profile = profiles.find((p) => p.id === uid);
    navigate(`/vortexuserProfile/${uid}`, { state: { profile } });
  };

  const exportToExcel = () => {
    const data = filteredUsers.map((u) => ({
      Name: u.name || "",
      Email: u.email || "",
      Subscribed: SUBSCRIBED_USER_IDS.includes(u.id) ? "Yes" : "No",
      Blocked: u.isBlocked ? "Yes" : "No",
      Ultra: u.ultraActive ? "Yes" : "No",
      UltraExpiry: formatExpiry(u.ultraExpiry) || "",
      Premium: u.premiumActive ? "Yes" : "No",
      PremiumExpiry: formatExpiry(u.premiumExpiry) || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Users");
    XLSX.writeFile(wb, "Vortex_Users.xlsx");
  };

  return (
    <div style={{
      flex: 1,
      minHeight: "100vh",
      backgroundColor: "#374151",
      color: "white",
      padding: "24px 16px",
      boxSizing: "border-box",
      overflowX: "hidden",
    }}>
      <ToastContainer />

      {/* Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>Actions</Typography>

          {/* Ultra section */}
          <div style={{ marginBottom: "16px", padding: "12px", border: "1px solid #e5e7eb", borderRadius: "8px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
              <span style={{ fontWeight: 600, fontSize: "14px" }}>⚡ Ultra</span>
              {ultraActive && ultraExpiry && (
                <span style={{ fontSize: "12px", color: "#6b7280" }}>Expires: {formatExpiry(ultraExpiry)}</span>
              )}
              {ultraActive && (
                <span style={{ fontSize: "11px", backgroundColor: "#7c3aed", color: "white", padding: "2px 8px", borderRadius: "999px" }}>Active</span>
              )}
            </div>
            {ultraActive ? (
              <button
                onClick={() => removeUltra(userId)}
                style={{ padding: "7px 14px", borderRadius: "6px", border: "none", cursor: "pointer", backgroundColor: "#ef4444", color: "white", fontSize: "13px" }}
              >
                Remove Ultra
              </button>
            ) : (
              <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                <input
                  type="date"
                  min={todayStr()}
                  value={ultraExpiryInput}
                  onChange={(e) => setUltraExpiryInput(e.target.value)}
                  style={{ padding: "6px 10px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: "13px", outline: "none" }}
                />
                <button
                  onClick={() => grantUltra(userId)}
                  style={{ padding: "7px 14px", borderRadius: "6px", border: "none", cursor: "pointer", backgroundColor: "#7c3aed", color: "white", fontSize: "13px" }}
                >
                  Grant Ultra
                </button>
              </div>
            )}
          </div>

          {/* Premium section */}
          <div style={{ marginBottom: "16px", padding: "12px", border: "1px solid #e5e7eb", borderRadius: "8px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
              <span style={{ fontWeight: 600, fontSize: "14px" }}>💎 Premium</span>
              {premiumActive && premiumExpiry && (
                <span style={{ fontSize: "12px", color: "#6b7280" }}>Expires: {formatExpiry(premiumExpiry)}</span>
              )}
              {premiumActive && (
                <span style={{ fontSize: "11px", backgroundColor: "#0ea5e9", color: "white", padding: "2px 8px", borderRadius: "999px" }}>Active</span>
              )}
            </div>
            {premiumActive ? (
              <button
                onClick={() => removePremium(userId)}
                style={{ padding: "7px 14px", borderRadius: "6px", border: "none", cursor: "pointer", backgroundColor: "#ef4444", color: "white", fontSize: "13px" }}
              >
                Remove Premium
              </button>
            ) : (
              <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                <input
                  type="date"
                  min={todayStr()}
                  value={premiumExpiryInput}
                  onChange={(e) => setPremiumExpiryInput(e.target.value)}
                  style={{ padding: "6px 10px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: "13px", outline: "none" }}
                />
                <button
                  onClick={() => grantPremium(userId)}
                  style={{ padding: "7px 14px", borderRadius: "6px", border: "none", cursor: "pointer", backgroundColor: "#0ea5e9", color: "white", fontSize: "13px" }}
                >
                  Grant Premium
                </button>
              </div>
            )}
          </div>

          {/* User actions section */}
          <div style={{ padding: "12px", border: "1px solid #e5e7eb", borderRadius: "8px" }}>
            <span style={{ fontWeight: 600, fontSize: "14px", display: "block", marginBottom: "10px" }}>User Actions</span>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button
                onClick={() => blockOrUnblock(userId, isBlocked ? "unblock" : "block")}
                style={{ padding: "7px 14px", borderRadius: "6px", border: "none", cursor: "pointer", backgroundColor: "#1f2937", color: "white", fontSize: "13px" }}
              >
                {isBlocked ? "Unblock" : "Block"}
              </button>
              <button
                onClick={() => subscribUserProfile(userId, isSubcribed ? "true" : "false")}
                style={{ padding: "7px 14px", borderRadius: "6px", border: "none", cursor: "pointer", backgroundColor: "#1f2937", color: "white", fontSize: "13px" }}
              >
                {isSubcribed ? "Unsubscribe" : "Subscribe"}
              </button>
              <button
                onClick={() => deleteUserProfile(userId)}
                style={{ padding: "7px 14px", borderRadius: "6px", border: "none", cursor: "pointer", backgroundColor: "#dc2626", color: "white", fontSize: "13px" }}
              >
                Delete
              </button>
            </div>
          </div>

        </Box>
      </Modal>

      {/* HEADER */}
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "12px",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "20px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <h1 style={{ margin: 0, fontSize: "clamp(1.25rem, 4vw, 2rem)", textTransform: "capitalize", fontWeight: 700 }}>
            Users
          </h1>
          <div style={{ border: "0.6px solid white", borderRadius: "6px", padding: "8px 12px", fontSize: "14px" }}>
            Users: {profiles.length}
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
          <input
            type="text"
            placeholder="Search by name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: "none",
              fontSize: "14px",
              width: "100%",
              maxWidth: "260px",
              boxSizing: "border-box",
              outline: "none",
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <label style={{ fontSize: "14px", whiteSpace: "nowrap" }}>Filter:</label>
            <select
              onChange={handleFilterChange}
              value={searchParams.get("filter") || "all"}
              style={{ padding: "8px", borderRadius: "6px", border: "none", fontSize: "14px", outline: "none" }}
            >
              <option value="all">All</option>
              <option value="subscribed">Subscribed</option>
              <option value="not_subscribed">Not Subscribed</option>
              <option value="face_verified">Face Verified</option>
              <option value="id_verified">ID Verified</option>
            </select>
          </div>
          <button
            onClick={exportToExcel}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: "none",
              cursor: "pointer",
              backgroundColor: "#16a34a",
              color: "white",
              fontSize: "14px",
              whiteSpace: "nowrap",
              fontWeight: 500,
            }}
          >
            Export Excel
          </button>
        </div>
      </div>

      {/* USER LIST */}
      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
          <CircleLoader color="#4A90E2" size={50} />
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px", maxHeight: "70vh", overflowY: "auto" }}>
          {filteredUsers.length > 0 ? filteredUsers.map((profile) => (
            <div
              key={profile.id}
              style={{
                backgroundColor: "#1f2937",
                borderRadius: "8px",
                padding: "14px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <div
                onClick={() => fetchSingleProfile(profile.id)}
                style={{ display: "flex", alignItems: "center", gap: "16px", flex: 1, minWidth: 0, cursor: "pointer" }}
              >
                <Avatar
                  src={profile.imageUrl || ""}
                  alt={profile.name}
                  sx={{ width: 48, height: 48, flexShrink: 0 }}
                  imgProps={{ onError: (e) => { e.target.src = ""; } }}
                />
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", fontWeight: 600, fontSize: "15px" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {profile.name}
                    </span>
                    {SUBSCRIBED_USER_IDS.includes(profile.id) && (
                      <span style={{ fontSize: "12px", color: "#fbbf24" }}>⭐ Prime</span>
                    )}
                    {profile.ultraActive && (
                      <span style={{ fontSize: "11px", backgroundColor: "#7c3aed", color: "white", padding: "2px 7px", borderRadius: "999px", fontWeight: 700, letterSpacing: "0.05em" }}>⚡ULTRA</span>
                    )}
                    {profile.premiumActive && (
                      <span style={{ fontSize: "11px", backgroundColor: "#0ea5e9", color: "white", padding: "2px 7px", borderRadius: "999px", fontWeight: 700, letterSpacing: "0.05em" }}>💎PLUS</span>
                    )}
                    {!!profile.personaVerified && (
                      <span style={{ fontSize: "11px", backgroundColor: "#059669", color: "white", padding: "2px 7px", borderRadius: "999px", fontWeight: 700, letterSpacing: "0.05em" }}>🛡️ Verified</span>
                    )}
                    {!!profile.azureVerified && (
                      <span style={{ fontSize: "11px", backgroundColor: "#d97706", color: "white", padding: "2px 7px", borderRadius: "999px", fontWeight: 700, letterSpacing: "0.05em" }}>😊</span>
                    )}
                  </div>
                  <div style={{ fontSize: "13px", color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "220px" }}>
                    {profile.email}
                  </div>
                </div>
              </div>

              <button
                onClick={() => handleOpen(profile.id, SUBSCRIBED_USER_IDS.includes(profile.id), profile.isBlocked, profile)}
                style={{ background: "none", border: "1px solid #4b5563", borderRadius: "6px", color: "white", padding: "6px 14px", cursor: "pointer", fontSize: "13px", flexShrink: 0 }}
              >
                Actions
              </button>
            </div>
          )) : (
            <div style={{ textAlign: "center", padding: "40px", color: "#9ca3af" }}>No users found.</div>
          )}
        </div>
      )}

      {/* REPORTED USERS */}
      <div style={{ marginTop: "32px" }}>
        <ReportedUsers />
      </div>
    </div>
  );
};

export default User;
